import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PRO_SUBSCRIPTION_FEATURES } from '../../constants/aiModels';
import { trackBeginCheckout, trackAddPaymentInfo } from '../../utils/analytics';
import {
  trackGroup50Event,
  trackPaymentAbandoned,
  trackPaymentCancelled,
  trackPaymentFailed,
  trackPaymentInitFailed,
  trackPaymentPageView,
  trackPaymentStart,
  trackPro50CheckoutView,
} from '../../utils/eventTracker';
import { buildGroup50LandingUrl, isGroup50EventActive } from '../../utils/group50Campaign';
import { getCurrentSeason, getNewYearPrices, getMaxDiscountLabel } from '../../config/seasonConfig';
import {
  PRO50_BASE_PRICES,
  PRO50_EVENT_END_LABEL,
  getPro50DiscountPercent,
  getPro50Price,
  isPro50Active,
} from '../../utils/pro50Campaign';
import { isInApp } from '../../utils/isInApp';
import AppPaymentBlocker from '../../components/AppPaymentBlocker';
import { useToast } from '../../components/Toast';
import './ProSubscription.css';

// 왓퀴즈 시즌 한정 고정 할인가 (cutover 시간 분기, 백엔드 NEWYEAR2026 쿠폰과 1:1 매칭)
// 5/31 23:59:59까지: 50% / 99k, 6/1 00:00:00부터: 30% / 140k
// getNewYearPrices() = seasonConfig의 cutover 헬퍼 (단일 SSoT)
const SECRET_50_PRICES = {
  '1month': 24000,
  '3month': 49000,
  '6month': 69000,
  '12month': 99000,
};

// 결제 수단 정의
// - CARD: 기존 빌링키 플로우 유지 (프로덕션 검증 완료, 건드리지 않음)
// - TRANSFER / MOBILE_PHONE: 결제창 v1의 requestPayment 방식으로 연동 완료.
//   단, 토스페이먼츠와 해당 결제수단 추가 계약 승인 전까지 UI 비활성화.
//   계약 승인 시 아래 주석을 해제하면 즉시 활성화됨 (코드 변경 없음).
//   문의: 토스페이먼츠 1599-4885 / https://dashboard.tosspayments.com
const PAYMENT_METHODS = [
  { id: 'CARD', label: '카드', badge: null, sdkMethod: '카드', flow: 'billing' },
  // { id: 'TRANSFER', label: '계좌이체', badge: null, sdkMethod: '계좌이체', flow: 'normal' },
  // { id: 'MOBILE_PHONE', label: '휴대폰', badge: null, sdkMethod: '휴대폰', flow: 'normal' }
];

const ProSubscription = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  
  const selectedPlan = searchParams.get('plan') || '6month';
  const promoCode = searchParams.get('promo') || '';
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentSystemReady, setIsPaymentSystemReady] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [paymentInitFailed, setPaymentInitFailed] = useState(false);
  const [paymentInitAttempt, setPaymentInitAttempt] = useState(1);
  const [paymentWidget, setPaymentWidget] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('CARD');
  const [verifiedPersonalOffer, setVerifiedPersonalOffer] = useState(null);
  const [personalOfferChecked, setPersonalOfferChecked] = useState(false);
  const [personalOfferError, setPersonalOfferError] = useState('');

  // 중복 클릭 방지
  const isSubmittingRef = useRef(false);
  const paymentStartedRef = useRef(false);
  const abandonmentTrackedRef = useRef(false);
  const pageEnteredAtRef = useRef(Date.now());
  const checkoutContextRef = useRef({
    plan: selectedPlan,
    price: 0,
    promoCode,
  });
  
  const getApiBaseUrl = () => {
    const currentUrl = window.location.hostname;
    if (currentUrl.includes('localhost')) {
      return 'http://localhost:8000';
    } else if (currentUrl.includes('whatquiztest')) {
      return 'https://quiz-api-test-397277209875.us-central1.run.app';
    } else {
      return 'https://quiz-api-397277209875.us-central1.run.app';
    }
  };

  // 토스 페이먼츠 SDK 로드
  useEffect(() => {
    const loadTossPaymentsSDK = () => {
      return new Promise((resolve, reject) => {
        if (window.TossPayments) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://js.tosspayments.com/v1/payment';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('토스 페이먼츠 SDK 로드 실패'));
        document.head.appendChild(script);
      });
    };

    const initializePaymentWidget = async () => {
      if (!currentUser) return;

      let stage = 'sdk_load';
      let httpStatus = null;
      try {
        setIsPaymentSystemReady(false);
        setPaymentInitFailed(false);
        setLoadingMessage('결제 시스템 준비 중...');
        
        await loadTossPaymentsSDK();

        stage = 'create_order';
        const API_BASE_URL = getApiBaseUrl();
        const idToken = await currentUser.getIdToken(true);
        
        const response = await fetch(`${API_BASE_URL}/api/payment/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({ plan_id: `pro_${selectedPlan}` })
        });

        if (!response.ok) {
          httpStatus = response.status;
          throw new Error('결제 정보를 가져오는데 실패했습니다.');
        }

        const result = await response.json();
        const { toss_config } = result.data;
        
        stage = 'toss_init';
        const tossPayments = window.TossPayments(toss_config.client_key);
        setPaymentWidget(tossPayments);
        setIsPaymentSystemReady(true);
        setLoadingMessage('');
      } catch (error) {
        console.error('결제위젯 초기화 실패:', error);
        trackPaymentInitFailed(selectedPlan, stage, error?.name, httpStatus, paymentInitAttempt);
        setIsPaymentSystemReady(false);
        setPaymentInitFailed(true);
        setLoadingMessage('결제 시스템 준비에 실패했습니다.');
      }
    };

    initializePaymentWidget();
  }, [currentUser, selectedPlan, paymentInitAttempt]);

  // 프로모션 할인
  const normalizedPromoCode = promoCode.toLowerCase();
  const isRenewalPromo = normalizedPromoCode === 'renewal20';
  const isTrialPromo = normalizedPromoCode === 'trial50';
  const isInviteWelcomePromo = normalizedPromoCode === 'invite_welcome';
  const isNewYearPromo = normalizedPromoCode === 'newyear2026';
  const isPrivate99Promo = normalizedPromoCode === 'private99';
  const isBrochureQrPromo = normalizedPromoCode === 'brochureqr2026';
  const isExhibitionPromo = normalizedPromoCode === 'exhibition2026';
  const isSecret50Promo = isPrivate99Promo || isBrochureQrPromo || isExhibitionPromo;
  const isPersonalOfferPromo = isRenewalPromo || isTrialPromo || isInviteWelcomePromo;
  const isPromoActive = isPersonalOfferPromo || isNewYearPromo || isSecret50Promo;
  const promoDiscountRate = isTrialPromo ? 0.5 : (isRenewalPromo ? 0.2 : 0);

  // 개인 오퍼는 URL 파라미터를 신뢰하지 않고 서버 자격·가격을 다시 확인한다.
  useEffect(() => {
    if (!isPersonalOfferPromo) {
      setVerifiedPersonalOffer(null);
      setPersonalOfferError('');
      setPersonalOfferChecked(true);
      return undefined;
    }
    if (!currentUser) {
      setPersonalOfferChecked(false);
      return undefined;
    }

    let cancelled = false;
    setPersonalOfferChecked(false);
    setVerifiedPersonalOffer(null);
    setPersonalOfferError('');

    const verifyPersonalOffer = async () => {
      try {
        const idToken = await currentUser.getIdToken(true);
        const response = await fetch(`${getApiBaseUrl()}/api/payment/my-offers`, {
          headers: { 'Authorization': `Bearer ${idToken}` }
        });
        if (!response.ok) {
          throw new Error('할인 혜택을 확인하지 못했어요.');
        }
        const result = await response.json();
        const matchedOffer = (result.offers || []).find(
          (offer) => offer.id === normalizedPromoCode
        );
        if (!matchedOffer) {
          throw new Error('이 혜택은 현재 계정에서 사용할 수 없어요.');
        }
        if (!cancelled) setVerifiedPersonalOffer(matchedOffer);
      } catch (offerError) {
        if (!cancelled) {
          setPersonalOfferError(
            offerError.message || '할인 혜택을 확인하지 못했어요.'
          );
        }
      } finally {
        if (!cancelled) setPersonalOfferChecked(true);
      }
    };

    verifyPersonalOffer();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isPersonalOfferPromo, normalizedPromoCode]);
  
  const subscriptionPlans = useMemo(() => {
    // cutover 시간 분기: 5/31까지 99k, 6/1부터 140k (백엔드 NEWYEAR2026과 1:1 동기화)
    const NEW_YEAR_PRICES = getNewYearPrices();
    
    const applyPromo = (planKey, originalPrice) => {
      if (isPersonalOfferPromo) {
        return verifiedPersonalOffer?.prices?.[planKey]?.final_price ?? originalPrice;
      }
      if (isSecret50Promo) {
        return SECRET_50_PRICES[planKey] || originalPrice;
      }
      if (isNewYearPromo) {
        return NEW_YEAR_PRICES[planKey];
      }
      if (isPromoActive) {
        return Math.floor(originalPrice * (1 - promoDiscountRate));
      }
      return originalPrice;
    };
    
    const getDiscountText = (planKey, originalPrice) => {
      if (isPersonalOfferPromo) {
        const discountRate = verifiedPersonalOffer?.prices?.[planKey]?.discount_rate;
        return discountRate > 0 ? `${discountRate}%` : null;
      }
      if (isSecret50Promo) {
        const discountRate = Math.round((1 - SECRET_50_PRICES[planKey] / originalPrice) * 100);
        return discountRate > 0 ? `${discountRate}%` : null;
      }
      if (isNewYearPromo) {
        const discountRate = Math.round((1 - NEW_YEAR_PRICES[planKey] / originalPrice) * 100);
        return discountRate > 0 ? `${discountRate}%` : null;
      }
      if (isTrialPromo) return '50%';
      if (isRenewalPromo) return '20%';
      return null;
    };
    
    // 기존 프로모션 (trial50, renewal20)에서는 1개월도 할인 적용
    const isOtherPromo = isPersonalOfferPromo
      ? Boolean(verifiedPersonalOffer)
      : isPromoActive && !isNewYearPromo && !isSecret50Promo;
    const hasPromoDiscountForPlan = (planKey) => {
      if (isPersonalOfferPromo) {
        const pricing = verifiedPersonalOffer?.prices?.[planKey];
        return Boolean(pricing && pricing.final_price < pricing.original_price);
      }
      if (isSecret50Promo) return planKey !== '1month';
      if (isNewYearPromo) return planKey !== '1month';
      return isPromoActive;
    };
    
    // 10월 PRO 최대 50% 이벤트: 쿠폰가와 이벤트가 중 싼 쪽 하나 (서버 _calculate_expected_payment_amount와 동일)
    const withPro50 = (planKey, plan) => {
      const eventPrice = getPro50Price(planKey);
      if (eventPrice == null || eventPrice > plan.price) return plan;
      return {
        ...plan,
        price: eventPrice,
        originalPrice: PRO50_BASE_PRICES[planKey],
        discount: `${getPro50DiscountPercent(planKey)}%`,
        isPro50: true,
      };
    };

    const plans = {
      '1month': {
        id: 'pro_1month',
        name: '1개월',
        // newyear2026/private99 등 비밀 특가에서는 1개월 할인 없음 (원가 그대로)
        price: (isNewYearPromo || isSecret50Promo) ? 24000 : applyPromo('1month', 24000),
        originalPrice: isOtherPromo ? 24000 : null,
        period: '1개월',
        discount: isOtherPromo ? getDiscountText('1month', 24000) : null,
        features: PRO_SUBSCRIPTION_FEATURES
      },
      '3month': {
        id: 'pro_3month',
        name: '3개월',
        price: applyPromo('3month', 60000),
        originalPrice: hasPromoDiscountForPlan('3month') ? 60000 : 72000,
        period: '3개월',
        discount: hasPromoDiscountForPlan('3month') ? getDiscountText('3month', 60000) : '17%',
        features: PRO_SUBSCRIPTION_FEATURES
      },
      '6month': {
        id: 'pro_6month',
        name: '6개월',
        price: applyPromo('6month', 110000),
        originalPrice: hasPromoDiscountForPlan('6month') ? 110000 : 144000,
        period: '6개월',
        discount: hasPromoDiscountForPlan('6month') ? getDiscountText('6month', 110000) : '24%',
        features: PRO_SUBSCRIPTION_FEATURES
      },
      '12month': {
        id: 'pro_12month',
        name: '12개월',
        price: applyPromo('12month', 200000),
        originalPrice: hasPromoDiscountForPlan('12month') ? 200000 : 288000,
        period: '12개월',
        discount: hasPromoDiscountForPlan('12month') ? getDiscountText('12month', 200000) : '31%',
        features: PRO_SUBSCRIPTION_FEATURES
      }
    };
    return Object.fromEntries(
      Object.entries(plans).map(([planKey, plan]) => [planKey, withPro50(planKey, plan)])
    );
  }, [
    isPromoActive,
    isNewYearPromo,
    isSecret50Promo,
    isTrialPromo,
    isRenewalPromo,
    isPersonalOfferPromo,
    promoDiscountRate,
    verifiedPersonalOffer,
  ]);

  // 10월 이벤트가가 적용된 개인 결제 페이지 도착 — Meta 최적화 이벤트(Pro50CheckoutView), 1회
  const pro50CheckoutTrackedRef = useRef(false);
  useEffect(() => {
    const plan = subscriptionPlans[selectedPlan];
    if (!plan?.isPro50 || pro50CheckoutTrackedRef.current) return;
    if (isPersonalOfferPromo && !personalOfferChecked) return;
    pro50CheckoutTrackedRef.current = true;
    trackPro50CheckoutView({
      plan: selectedPlan,
      quantity: 1,
      value: plan.price,
      checkoutType: 'personal',
    });
  }, [subscriptionPlans, selectedPlan, isPersonalOfferPromo, personalOfferChecked]);

  // 페이지뷰 트래킹
  const hasTrackedPageView = useRef(false);
  useEffect(() => {
    if (isPersonalOfferPromo && (!personalOfferChecked || !verifiedPersonalOffer)) {
      return;
    }
    if (!hasTrackedPageView.current && subscriptionPlans) {
      const source = searchParams.get('source') || 'direct';
      const plan = subscriptionPlans[selectedPlan];
      if (plan) {
        trackPaymentPageView(source, selectedPlan, plan.price, normalizedPromoCode || null);
      }
      hasTrackedPageView.current = true;
    }
  }, [
    searchParams,
    selectedPlan,
    subscriptionPlans,
    normalizedPromoCode,
    isPersonalOfferPromo,
    personalOfferChecked,
    verifiedPersonalOffer,
  ]);

  useEffect(() => {
    const plan = subscriptionPlans[selectedPlan];
    checkoutContextRef.current = {
      plan: selectedPlan,
      price: plan?.price || 0,
      promoCode: normalizedPromoCode || null,
    };
  }, [
    normalizedPromoCode,
    selectedPlan,
    subscriptionPlans,
  ]);

  useEffect(() => {
    const trackAbandonment = (reason) => {
      const elapsedSeconds = Math.floor(
        (Date.now() - pageEnteredAtRef.current) / 1000
      );
      if (
        abandonmentTrackedRef.current
        || paymentStartedRef.current
        || !hasTrackedPageView.current
        || elapsedSeconds < 2
      ) {
        return;
      }
      abandonmentTrackedRef.current = true;
      const context = checkoutContextRef.current;
      trackPaymentAbandoned(
        context.plan,
        context.price,
        context.promoCode,
        elapsedSeconds,
        reason
      );
    };
    const handlePageHide = () => trackAbandonment('pagehide');
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      trackAbandonment('route_change');
    };
  }, []);

  // API 기본 URL (결제창 v1 NORMAL 플로우에서 주문번호 생성용)
  const getApiBaseUrlForOrder = () => {
    const host = window.location.hostname;
    if (host.includes('localhost')) return 'http://localhost:8000';
    if (host.includes('whatquiztest')) return 'https://quiz-api-test-397277209875.us-central1.run.app';
    return 'https://quiz-api-397277209875.us-central1.run.app';
  };

  const handlePayment = async () => {
    if (!currentUser) {
      showToast('로그인하면 PRO를 시작할 수 있어요', 'info');
      navigate('/login');
      return;
    }

    if (isPersonalOfferPromo && !personalOfferChecked) {
      showToast('할인 혜택을 확인하고 있어요. 잠시만 기다려주세요', 'info');
      return;
    }

    if (isPersonalOfferPromo && !verifiedPersonalOffer) {
      showToast(personalOfferError || '이 혜택은 현재 계정에서 사용할 수 없어요', 'warning', 5000);
      return;
    }

    if (!paymentWidget || !isPaymentSystemReady) {
      showToast('결제 화면을 준비하고 있어요. 잠시 후 다시 시도해주세요', 'info');
      return;
    }

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setIsLoading(true);

    try {
      const plan = subscriptionPlans[selectedPlan];
      const finalAmount = plan.price;
      const originalAmount = plan.originalPrice || plan.price;
      const method = PAYMENT_METHODS.find(m => m.id === selectedMethod) || PAYMENT_METHODS[0];

      paymentStartedRef.current = true;
      trackBeginCheckout(plan.id, finalAmount);
      trackPaymentStart(selectedPlan, originalAmount, finalAmount, normalizedPromoCode || null);
      trackAddPaymentInfo(method.id.toLowerCase());

      if (typeof window.fbq !== 'undefined') {
        const _utm = (() => {
          try {
            const s = localStorage.getItem('whatquiz_utm');
            if (!s) return {};
            const p = JSON.parse(s);
            return (!p.expiresAt || Date.now() <= p.expiresAt) ? p : {};
          } catch {
            return {};
          }
        })();
        window.fbq('track', 'InitiateCheckout', {
          value: finalAmount, currency: 'KRW', content_ids: [plan.id],
          utm_source: _utm.utm_source || undefined,
          utm_medium: _utm.utm_medium || undefined,
          utm_campaign: _utm.utm_campaign || undefined,
          utm_content: _utm.utm_content || undefined,
        });
      }

      // 분기: 카드는 기존 빌링키 플로우 / 계좌이체·휴대폰은 신규 일반결제 플로우
      if (method.flow === 'billing') {
        // ===== 기존 검증된 카드 빌링키 플로우 (건드리지 않음) =====
        const customerKey = `WHATQUIZ_${currentUser.uid}_${Date.now()}`;
        let successUrl = `${window.location.origin}/billing/success?amount=${finalAmount}&planId=${plan.id}`;
        if (normalizedPromoCode) successUrl += `&promo=${normalizedPromoCode}`;

        // 취소·실패 화면에서 '다시 등록하기'가 고른 기간과 할인을 그대로 이어가도록 실패 URL에도 같이 싣는다.
        let failUrl = `${window.location.origin}/billing/fail?planId=${plan.id}&amount=${finalAmount}`;
        if (normalizedPromoCode) failUrl += `&promo=${normalizedPromoCode}`;

        await paymentWidget.requestBillingAuth(method.sdkMethod, {
          customerKey,
          successUrl,
          failUrl
        });
      } else {
        // ===== 신규 일반결제 플로우 (requestPayment) =====
        // 주문번호는 백엔드에서 생성 (중복·경합 방지)
        const idToken = await currentUser.getIdToken(true);
        const orderRes = await fetch(`${getApiBaseUrlForOrder()}/api/payment/create-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({ plan_id: plan.id })
        });

        if (!orderRes.ok) {
          throw new Error('주문번호 생성에 실패했어요. 잠시 후 다시 시도해주세요.');
        }
        const orderData = await orderRes.json();
        const orderId = orderData.data.order_id;

        let successUrl = `${window.location.origin}/payment/widget-success?planId=${plan.id}&amount=${finalAmount}`;
        if (normalizedPromoCode) successUrl += `&promo=${normalizedPromoCode}`;

        await paymentWidget.requestPayment(method.sdkMethod, {
          amount: finalAmount,
          orderId,
          orderName: `왓퀴즈 PRO ${plan.period}`,
          successUrl,
          failUrl: `${window.location.origin}/payment/widget-fail`,
          customerEmail: currentUser.email,
          customerName: currentUser.displayName || currentUser.email.split('@')[0]
        });
      }
    } catch (error) {
      console.error('결제 요청 중 오류:', error);

      const plan = subscriptionPlans[selectedPlan];

      if (error.code === 'USER_CANCEL') {
        trackPaymentCancelled(selectedPlan, plan?.price || 0, 'checkout_window');
        showToast('결제를 취소했어요', 'info');
      } else {
        trackPaymentFailed(selectedPlan, plan?.price || 0, error.code || 'UNKNOWN', error.message || '알 수 없는 오류');
        showToast('결제를 처리하지 못했어요. 잠시 후 다시 시도해주세요', 'error', 5000);
      }

      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handlePlanChange = (planKey) => {
    const plan = subscriptionPlans[planKey];
    navigate(`/subscription/pro?plan=${planKey}&price=${plan.price}${normalizedPromoCode ? `&promo=${normalizedPromoCode}` : ''}`);
  };

  if (isInApp()) {
    return (
      <AppPaymentBlocker
        pageName="PRO 구독"
        webUrl={`https://whatquiz.co.kr/subscription/pro?plan=${selectedPlan}${normalizedPromoCode ? `&promo=${normalizedPromoCode}` : ''}`}
        fallbackPath="/pricing"
      />
    );
  }

  if (!currentUser) {
    const currentPath = `/subscription/pro?plan=${selectedPlan}${normalizedPromoCode ? `&promo=${normalizedPromoCode}` : ''}`;
    const redirectUrl = encodeURIComponent(currentPath);
    return (
      <div className="pro-subscription">
        <div className="pro-sub-login">
          <h2>{isNewYearPromo || isSecret50Promo ? '할인가로 결제하려면 로그인이 필요해요' : '로그인이 필요해요'}</h2>
          <p>
            {isNewYearPromo || isSecret50Promo
              ? '구글 계정으로 10초면 가입 완료! 가입 후 바로 할인가로 결제할 수 있어요.'
              : '로그인하면 바로 PRO를 결제할 수 있어요.'
            }
          </p>
          <button className="pro-sub-login-btn" onClick={() => navigate(`/auth?redirect=${redirectUrl}`)}>
            {isNewYearPromo || isSecret50Promo ? '구글로 10초 가입하고 결제하기' : '로그인하기'}
          </button>
        </div>
      </div>
    );
  }

  const currentPlan = subscriptionPlans[selectedPlan];
  const currentMethod = PAYMENT_METHODS.find(m => m.id === selectedMethod) || PAYMENT_METHODS[0];
  const selectedPersonalOfferPrice = verifiedPersonalOffer?.prices?.[selectedPlan];
  const personalOfferDiscountRate = selectedPersonalOfferPrice?.discount_rate;
  const personalOfferDiscountAmount = selectedPersonalOfferPrice?.discount_amount
    ?? (
      Number.isFinite(selectedPersonalOfferPrice?.original_price)
      && Number.isFinite(selectedPersonalOfferPrice?.final_price)
        ? selectedPersonalOfferPrice.original_price - selectedPersonalOfferPrice.final_price
        : 0
    );
  const personalOfferTitle = isInviteWelcomePromo
    ? '친구 초대 첫 결제 혜택'
    : (isTrialPromo ? '체험 첫 결제 혜택' : '연장 고객 혜택');
  const personalOfferBadge = isInviteWelcomePromo && personalOfferDiscountAmount > 0
    ? `${personalOfferDiscountAmount.toLocaleString()}원 할인`
    : `${personalOfferDiscountRate || 0}% 할인`;
  const isPersonalOfferPending = isPersonalOfferPromo && !personalOfferChecked;
  const isPersonalOfferBlocked = (
    isPersonalOfferPromo && personalOfferChecked && !verifiedPersonalOffer
  );

  return (
    <div className="pro-subscription">
      <div className="pro-subscription-inner">
        {/* 헤더 */}
        <div className="pro-sub-header">
          <h1>왓퀴즈 PRO</h1>
          <p>최신 AI 모델을 무제한으로</p>
        </div>

        {/* 10월 PRO 최대 50% 이벤트 — 이벤트가가 적용된 플랜에서만 */}
        {currentPlan.isPro50 && !isPersonalOfferPending && (
          <div className="pro-sub-promo" data-testid="pro-sub-pro50-banner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#3182f6">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
            </svg>
            <span className="pro-sub-promo-text">10월 이벤트 · {PRO50_EVENT_END_LABEL}</span>
            <span className="pro-sub-promo-badge">{currentPlan.discount} 할인</span>
          </div>
        )}

        {/* 프로모션 */}
        {isPromoActive && !currentPlan.isPro50 && (!isPersonalOfferPromo || verifiedPersonalOffer) && (
          <div className="pro-sub-promo">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#3182f6">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
            </svg>
            <span className="pro-sub-promo-text">
              {isSecret50Promo
                ? '비밀 링크 전용 특가'
                : (isNewYearPromo ? getCurrentSeason().label : personalOfferTitle)}
            </span>
            <span className="pro-sub-promo-badge">
              {isPersonalOfferPromo
                ? personalOfferBadge
                : (isSecret50Promo
                  ? '최대 50% 할인'
                  : (isNewYearPromo ? getMaxDiscountLabel() : ''))}
            </span>
          </div>
        )}

        {isPersonalOfferBlocked && (
          <div className="pro-sub-offer-error" role="alert">
            <strong>{personalOfferError}</strong>
            <span>마이페이지에서 지금 받을 수 있는 혜택을 확인해주세요.</span>
            <button type="button" onClick={() => navigate('/subscription')}>
              혜택 확인하기
            </button>
          </div>
        )}
        

        {/* 선택된 플랜 */}
        <div className="pro-sub-plan">
          <h3 className="pro-sub-plan-name">왓퀴즈 PRO {currentPlan.period}</h3>

          {isPersonalOfferPending ? (
            <div className="pro-sub-offer-loading" role="status">
              <span>할인 가격을 확인하고 있어요</span>
              <div className="pro-sub-offer-loading-bar" />
            </div>
          ) : (
            <>
              <div className="pro-sub-plan-price">
                <span className="pro-sub-plan-amount">{currentPlan.price.toLocaleString()}</span>
                <span className="pro-sub-plan-currency">원</span>
                <span className="pro-sub-plan-period">/{currentPlan.period}</span>
              </div>

              {currentPlan.originalPrice && (
                <p className="pro-sub-plan-original">
                  <del>{currentPlan.originalPrice.toLocaleString()}원</del>
                  <span>{currentPlan.discount} 할인</span>
                </p>
              )}
            </>
          )}

          <ul className="pro-sub-features">
            {currentPlan.features.map((feature, index) => (
              <li key={index}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* 기간 선택 */}
        {!isPersonalOfferPending && (
        <div className="pro-sub-periods">
          <p className="pro-sub-periods-label">다른 기간 선택</p>
          <div className="pro-sub-periods-grid">
            {Object.entries(subscriptionPlans).map(([key, plan]) => (
              <button
                key={key}
                className={`pro-sub-period-btn ${selectedPlan === key ? 'active' : ''}`}
                onClick={() => handlePlanChange(key)}
              >
                <span className="pro-sub-period-duration">{plan.period}</span>
                <span className="pro-sub-period-price">{(plan.price / 10000).toFixed(plan.price % 10000 === 0 ? 0 : 1)}만원</span>
              </button>
            ))}
          </div>
        </div>
        )}

        {/* 결제 수단 선택 (2개 이상일 때만 노출) */}
        {PAYMENT_METHODS.length > 1 && (
          <div className="pro-sub-methods">
            <p className="pro-sub-methods-label">결제 수단</p>
            <div className="pro-sub-methods-grid">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`pro-sub-method-btn ${selectedMethod === m.id ? 'active' : ''}`}
                  onClick={() => setSelectedMethod(m.id)}
                  aria-pressed={selectedMethod === m.id}
                >
                  <span className="pro-sub-method-label">{m.label}</span>
                  {m.badge && <span className="pro-sub-method-badge">{m.badge}</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 결제 버튼 — 결제창 준비가 실패했으면 같은 자리에서 다시 시도한다 */}
        {paymentInitFailed && !isLoading ? (
          <button
            className="pro-sub-pay-btn"
            data-testid="pro-sub-pay-retry"
            onClick={() => setPaymentInitAttempt((prev) => prev + 1)}
            type="button"
          >
            <span>결제 준비 다시 시도</span>
            <span className="pro-sub-pay-btn-sub">결제 시스템 준비에 실패했어요</span>
          </button>
        ) : (
        <button
          className="pro-sub-pay-btn"
          onClick={handlePayment}
          disabled={
            isLoading
            || !isPaymentSystemReady
            || isPersonalOfferPending
            || isPersonalOfferBlocked
          }
        >
          {isLoading || !isPaymentSystemReady || isPersonalOfferPending ? (
            <span className="pro-sub-pay-loading">
              <span className="pro-sub-spinner"></span>
              {isLoading
                ? '결제 처리 중...'
                : (isPersonalOfferPending ? '할인 확인 중...' : (loadingMessage || '준비 중...'))}
            </span>
          ) : isPersonalOfferBlocked ? (
            <span>사용할 수 없는 혜택이에요</span>
          ) : (
            <>
              <span>{currentPlan.price.toLocaleString()}원 결제하기</span>
              <span className="pro-sub-pay-btn-sub">{currentMethod.label}로 결제</span>
            </>
          )}
        </button>
        )}

        {/* 10월 이벤트 — 여러 명이면 단체 결제도 같은 가격 (결제 흐름을 막지 않는 보조 링크) */}
        {isPro50Active() && (
          <Link
            className="pro-sub-group50-link"
            data-testid="pro-sub-pro50-bulk-link"
            to={`/bulk-purchase?promo=pro50&plan=${selectedPlan}&qty=1`}
          >
            <span className="pro-sub-group50-link-muted">선생님 여러 분이면 · </span>
            <span className="pro-sub-group50-link-accent">
              단체 결제도 같은 가격
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </span>
          </Link>
        )}

        {/* 단체 50% 이벤트(9월) — 결제 버튼과 시각적으로 분리된 보조 텍스트 링크 (결제 흐름을 막지 않음) */}
        {isGroup50EventActive() && !isPro50Active() && (
          <Link
            className="pro-sub-group50-link"
            data-testid="pro-sub-group50-link"
            to={buildGroup50LandingUrl('checkout')}
            onClick={() => trackGroup50Event('crosslink_clicked', { stage: 'checkout' })}
          >
            <span className="pro-sub-group50-link-muted">10명 이상이면 1인당 반값 · </span>
            <span className="pro-sub-group50-link-accent">
              단체 이벤트 보기
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </span>
          </Link>
        )}

        {/* 결제 안내 */}
        <div className="pro-sub-info">
          <h4 className="pro-sub-info-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            결제 안내
          </h4>
          <ul className="pro-sub-info-list">
            <li><strong>자동 결제 없음</strong> · 선택한 기간만 이용해요</li>
            <li>신용카드 · 체크카드 결제 가능</li>
            <li>결제 후 <strong>거래명세서·영수증</strong>을 바로 받을 수 있어요</li>
            <li>토스페이먼츠 보안 결제 (PCI-DSS 인증)</li>
            <li>결제 후 <strong>7일 이내 100% 환불</strong> 가능</li>
            <li>만료 7일 전부터 마이페이지에서 연장 혜택을 확인할 수 있어요</li>
          </ul>
        </div>

        {/* 법적 링크 */}
        <div className="pro-sub-legal">
          <div className="pro-sub-legal-links">
            <a href="/terms-of-service">이용약관</a>
            <a href="/privacy-policy">개인정보처리방침</a>
            <a href="/refund-policy">환불정책</a>
          </div>
          <p className="pro-sub-legal-notice">결제 시 위 약관에 동의한 것으로 간주됩니다.</p>
        </div>
      </div>
    </div>
  );
};

export default ProSubscription;
